import type { LeaderboardEntry, LeaderboardPeriod, LeaderboardScope, Variant } from "@app/protocol";
import { useCallback, useEffect, useState } from "react";
import { Backdrop } from "../components/Backdrop";
import { GameChips, PeriodTabs, ScopeTabs } from "../components/BoardTabs";
import { CardDialog } from "../components/CardDialog";
import { IconButton } from "../components/IconButton";
import { Icon } from "../components/icons";
import { LeaderboardRow } from "../components/LeaderboardRow";
import { useProfile } from "../components/ProfileStrip";
import { type BoardState, useLeaderboard } from "../components/useLeaderboard";
import { useRankFrames } from "../components/useRankFrames";
import { useT } from "../i18n/context";
import { useStore } from "../store";
import { timeLeft } from "./leaderboardText";

/** The clock for the countdown, a tick a minute. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

type Open = (entry: LeaderboardEntry) => void;

function Body({ state, onOpen }: { state: BoardState & { retry: () => void }; onOpen: Open }) {
  const { t } = useT();
  const rankOf = useRankFrames(state.board?.variant ?? "batak");
  if (state.board === null && state.status === "failed")
    return (
      <div className="flex flex-col items-start gap-3">
        <p>{t("leaderboard.error")}</p>
        <button
          type="button"
          className="btn-gold h-11 rounded-pill px-5 text-[14px] font-extrabold"
          onClick={state.retry}
        >
          {t("leaderboard.retry")}
        </button>
      </div>
    );
  if (state.board === null) return <p className="text-ink/75">{t("leaderboard.loading")}</p>;
  if (state.board.entries.length === 0)
    return (
      <p className="rounded-[18px] border-[1.5px] border-ink/14 bg-panel-strong p-5 text-ink/85">
        {t(state.board.scope === "guild" ? "leaderboard.emptyGuild" : "leaderboard.empty")}
      </p>
    );
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0">
      {state.board.entries.map((entry) => (
        <li key={entry.userId}>
          <LeaderboardRow entry={entry} rank={rankOf(entry)} onOpen={() => onOpen(entry)} />
        </li>
      ))}
    </ol>
  );
}

/** Your own place, pinned over a fade at the foot of the screen (design; review 0012 S14). */
function YouBar({ board, onOpen }: { board: BoardState["board"]; onOpen: Open }) {
  const { t } = useT();
  const rankOf = useRankFrames(board?.variant ?? "batak");
  if (board === null) return null;
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[4] h-[200px] bg-linear-to-b from-transparent to-ground/85 to-55%"
      />
      <div className="fixed bottom-6 left-1/2 z-[5] flex w-[min(600px,calc(100vw-40px))] -translate-x-1/2 flex-col gap-1.5">
        <span className="self-center rounded-pill border border-ink/25 bg-panel-solid px-2.5 py-[3px] text-[11px] font-bold uppercase tracking-[0.1em]">
          {t("leaderboard.you")}
        </span>
        {board.you === null ? (
          <p className="rounded-[16px] border-[1.5px] border-gold bg-panel-solid px-3.5 py-3 text-[14px] shadow-you">
            {t("leaderboard.unranked")}
          </p>
        ) : (
          <LeaderboardRow
            entry={board.you}
            mine
            rank={rankOf(board.you)}
            onOpen={() => board.you && onOpen(board.you)}
          />
        )}
      </div>
    </>
  );
}

/** Everyone's ranking, one game and one period (design "Liderlik tablosu"). See docs/ekranlar/liderlik.md. */
export function Leaderboard({ onBack }: { onBack: () => void }) {
  const { t } = useT();
  const member = useStore((s) => s.member);
  const profile = useProfile(member?.userId ?? null);
  const [period, setPeriod] = useState<LeaderboardPeriod>("week");
  const [picked, setPicked] = useState<Variant | null>(null);
  const [scope, setScope] = useState<LeaderboardScope>("all");
  // Opens on the game you played last (owner, 12 Sept), Batak before your first match.
  const variant = picked ?? profile?.recent[0]?.variant ?? "batak";
  const state = useLeaderboard(period, variant, scope);
  const [card, setCard] = useState<LeaderboardEntry | null>(null);
  const closeCard = useCallback(() => setCard(null), []);
  const rankOf = useRankFrames(variant);
  const left = timeLeft(t, state.board?.period === period ? state.board.endsAt : null, useMinute());
  const heading = t(`leaderboard.heading.${period}`);
  return (
    <div className="relative isolate h-full overflow-y-auto font-ui text-ink">
      <Backdrop />
      <div className="relative mx-auto flex max-w-[600px] flex-col gap-4 px-5 pb-[200px] pt-6">
        <header className="flex items-center gap-3.5">
          <IconButton label={t("leaderboard.back")} size={52} onClick={onBack}>
            <Icon name="back" strokeWidth={2.4} />
          </IconButton>
          <div className="flex min-w-0 flex-col gap-0.5">
            <h1 className="text-legible text-[26px] font-extrabold leading-[1.1] tracking-[-0.01em]">
              {t("leaderboard.title")}
            </h1>
            <p className="text-[13px] text-ink/70">
              {left === null ? heading : `${heading} · ${left}`}
            </p>
          </div>
        </header>
        <div className="flex flex-col gap-2.5">
          {state.board?.guildKnown === true ? <ScopeTabs scope={scope} onPick={setScope} /> : null}
          <PeriodTabs period={period} onPick={setPeriod} />
          <GameChips variant={variant} onPick={setPicked} />
        </div>
        <Body state={state} onOpen={setCard} />
      </div>
      <YouBar board={state.board} onOpen={setCard} />
      {card === null ? null : <CardDialog member={card} rank={rankOf(card)} onClose={closeCard} />}
    </div>
  );
}
